import {TFunction} from 'i18next';
import {
  CLINICAL_SUPPORT__EXAMS_ID,
  CLINICAL_SUPPORT__HEALTH_APPOINTMENTS_ID,
  CLINICAL_SUPPORT__MEDICATION_ID,
  CLINICAL_SUPPORT__NURSING_ID,
  CLINICAL_SUPPORT__OTHER_ID,
  CLINICAL_SUPPORT__PHYSIOTHERAPY_ID,
  CLINICAL_SUPPORT__SURGERY_ID,
  CLINICAL_SUPPORT__VACCINATION_ID,
  CLINICAL_SUPPORT__VITAL_SIGNS_ID,
  BADL__BATH_ID,
  BADL__CLOTHING_ID,
  BADL__ELIMINATION_ID,
  BADL__FOOD_ID,
  BADL__OTHER_ID,
  BADL__PERSONAL_HYGIENE_ID,
  BADL__POSITION_MOBILITY_ID,
  BADL__SLEEP_ID,
  IADL__ESCORT_ID,
  IADL__HOUSEKEEPING_ID,
  IADL__LAUNDRY_ID,
  IADL__OTHER_ID,
  IADL__SHOPPING_ID,
  IADL__TRANSPORT_ID,
  PERSONAL_DEVELOPMENT__COGNITIVE_INTELLECTUAL_ID,
  PERSONAL_DEVELOPMENT__OTHER_ID,
  PERSONAL_DEVELOPMENT__PHYSICS_MOVEMENT_ID,
  PERSONAL_DEVELOPMENT__RECREATIONAL_ARTISTIC_ID,
  PERSONAL_DEVELOPMENT__RELIGION_SPIRITUALITY_ID,
  PERSONAL_DEVELOPMENT__DAILY_OCCUPATION_ID,
  PERSONAL_DEVELOPMENT__SENSORY_ID,
  PERSONAL_DEVELOPMENT__SOCIOCULTURAL_ID,
  PERSONAL_DEVELOPMENT__SOCIAL_INTERACTION_ID,
} from '../../../../constants';
import {exportCarePlanDebugData} from './export-care-plan-debug-data';
import {ExportCarePlanGroupDataProcessor} from './export-care-plan-group-data-processor';
import {exportCarePlanProcessReportData} from './export-care-plan-process-report-data';
import {
  ExportCarePlanData,
  ExportCarePlanReportData,
  ExportCarePlanTableData,
} from './export-care-plan-types';
import {MedicationPdfDataProcessor} from '../clinical-support/medication/export-care-plan-medication-pdf-data';
import {VitalSignsPdfDataProcessor} from '../clinical-support/vital-signs/export-care-plan-vital-signs-pdf-data';
import {HealthAppointmentsPdfDataProcessor} from '../clinical-support/health-appointments/export-care-plan-health-appointments-pdf-data';
import {NursingPdfDataProcessor} from '../clinical-support/nursing/export-care-plan-nursing-pdf-data';
import {VaccinationPdfDataProcessor} from '../clinical-support/vaccination/export-care-plan-vaccination-pdf-data';
import {ExamsPdfDataProcessor} from '../clinical-support/exams/export-care-plan-exams-pdf-data';
import {PhysiotherapyPdfDataProcessor} from '../clinical-support/physiotherapy/export-care-plan-physiotherapy-pdf-data';
import {SurgeryPdfDataProcessor} from '../clinical-support/surgery/export-care-plan-surgery-pdf-data';
import {OtherPdfDataProcessor} from '../clinical-support/other/export-care-plan-other-pdf-data';
import {CognitiveIntellectualPdfDataProcessor} from '../personal-development/cognitive-intellectual/export-care-plan-cognitive-intellectual-pdf-data';
import {PersonalDevelopmentOtherPdfDataProcessor} from '../personal-development/other/export-care-plan-other-pdf-data';
import {PhysicsMovementPdfDataProcessor} from '../personal-development/physics-movement/export-care-plan-physics-movement-pdf-data';
import {RecreationalArtisticPdfDataProcessor} from '../personal-development/recreational-artistic/export-care-plan-recreational-artistic-pdf-data';
import {SocialInteractionPdfDataProcessor} from '../personal-development/social-interaction/export-care-plan-social-interaction-pdf-data';
import {ReligionSpiritualityPdfDataProcessor} from '../personal-development/religion-spirituality/export-care-plan-religion-spirituality-pdf-data';
import {SocioculturalPdfDataProcessor} from '../personal-development/sociocultural/export-care-plan-sociocultural-pdf-data';
import {DailyOccupationPdfDataProcessor} from '../personal-development/daily-occupation/export-care-plan-daily-occupation-pdf-data';
import {SensoryPdfDataProcessor} from '../personal-development/sensory/export-care-plan-sensory-pdf-data';
import {TransportPdfDataProcessor} from '../iadl/transport/export-care-plan-transport-pdf-data';
import {LaundryPdfDataProcessor} from '../iadl/laundry/export-care-plan-laundry-pdf-data';
import {HousekeepingPdfDataProcessor} from '../iadl/housekeeping/export-care-plan-housekeeping-pdf-data';
import {EscortPdfDataProcessor} from '../iadl/escort/export-care-plan-escort-pdf-data';
import {ShoppingPdfDataProcessor} from '../iadl/shopping/export-care-plan-shopping-pdf-data';
import {AivdOtherPdfDataProcessor} from '../iadl/other/export-care-plan-other-pdf-data';
import {FoodPdfDataProcessor} from '../badl/food/export-care-plan-food-pdf-data';
import {ClothingPdfDataProcessor} from '../badl/clothing/export-care-plan-clothing-pdf-data';
import {PersonalHygienePdfDataProcessor} from '../badl/personal-hygiene/export-care-plan-personal-hygiene-pdf-data';
import {BathPdfDataProcessor} from '../badl/bath/export-care-plan-bath-pdf-data';
import {SleepPdfDataProcessor} from '../badl/sleep/export-care-plan-sleep-pdf-data';
import {PositionMobilityPdfDataProcessor} from '../badl/position-mobility/export-care-plan-position-mobility-pdf-data';
import {EliminationPdfDataProcessor} from '../badl/elimination/export-care-plan-elimination-pdf-data';
import {BadlOtherPdfDataProcessor} from '../badl/other/export-care-plan-other-pdf-data';

// const appConfig: AppConfig = require('../../../../../config.json');

export async function exportCarePlanActivitiesGroupPdfData(
  reportData: ExportCarePlanReportData,
  t: TFunction
): Promise<ExportCarePlanTableData> {
  // 1. Process report data
  const baseData = exportCarePlanProcessReportData(reportData);
  // 2. Process data for pdf (tables)
  const tableData = await exportCarePlanActivitiesGroupProcessData(baseData, t);
  // 3. Debug
  // if (appConfig.environment === 'development')
  exportCarePlanDebugData(reportData, baseData, tableData);

  // 4. Done
  return tableData;
}

// --------------------------------------------------------------------------
// --------------------------------------------------------------------------
// --------------------------------------------------------------------------

async function exportCarePlanActivitiesGroupProcessData(
  baseData: ExportCarePlanData,
  t: TFunction
): Promise<ExportCarePlanTableData> {
  switch (baseData.group.id) {
    case BADL__FOOD_ID:
      return new FoodPdfDataProcessor(baseData, t).process();

    case BADL__CLOTHING_ID:
      return new ClothingPdfDataProcessor(baseData, t).process();

    case BADL__PERSONAL_HYGIENE_ID:
      return new PersonalHygienePdfDataProcessor(baseData, t).process();

    case BADL__BATH_ID:
      return new BathPdfDataProcessor(baseData, t).process();

    case BADL__SLEEP_ID:
      return new SleepPdfDataProcessor(baseData, t).process();

    case BADL__POSITION_MOBILITY_ID:
      return new PositionMobilityPdfDataProcessor(baseData, t).process();

    case BADL__ELIMINATION_ID:
      return new EliminationPdfDataProcessor(baseData, t).process();

    case BADL__OTHER_ID:
      return new BadlOtherPdfDataProcessor(baseData, t).process();

    case IADL__TRANSPORT_ID:
      return new TransportPdfDataProcessor(baseData, t).process();

    case IADL__LAUNDRY_ID:
      return new LaundryPdfDataProcessor(baseData, t).process();

    case IADL__HOUSEKEEPING_ID:
      return new HousekeepingPdfDataProcessor(baseData, t).process();

    case IADL__ESCORT_ID:
      return new EscortPdfDataProcessor(baseData, t).process();

    case IADL__SHOPPING_ID:
      return new ShoppingPdfDataProcessor(baseData, t).process();

    case IADL__OTHER_ID:
      return new AivdOtherPdfDataProcessor(baseData, t).process();

    case CLINICAL_SUPPORT__MEDICATION_ID:
      return new MedicationPdfDataProcessor(baseData, t).process();

    case CLINICAL_SUPPORT__VITAL_SIGNS_ID:
      return new VitalSignsPdfDataProcessor(baseData, t).process();

    case CLINICAL_SUPPORT__HEALTH_APPOINTMENTS_ID:
      return new HealthAppointmentsPdfDataProcessor(baseData, t).process();

    case CLINICAL_SUPPORT__NURSING_ID:
      return new NursingPdfDataProcessor(baseData, t).process();

    case CLINICAL_SUPPORT__VACCINATION_ID:
      return new VaccinationPdfDataProcessor(baseData, t).process();

    case CLINICAL_SUPPORT__EXAMS_ID:
      return new ExamsPdfDataProcessor(baseData, t).process();

    case CLINICAL_SUPPORT__PHYSIOTHERAPY_ID:
      return new PhysiotherapyPdfDataProcessor(baseData, t).process();

    case CLINICAL_SUPPORT__SURGERY_ID:
      return new SurgeryPdfDataProcessor(baseData, t).process();

    case CLINICAL_SUPPORT__OTHER_ID:
      return new OtherPdfDataProcessor(baseData, t).process();

    case PERSONAL_DEVELOPMENT__COGNITIVE_INTELLECTUAL_ID:
      return new CognitiveIntellectualPdfDataProcessor(baseData, t).process();

    case PERSONAL_DEVELOPMENT__OTHER_ID:
      return new PersonalDevelopmentOtherPdfDataProcessor(baseData, t).process();

    case PERSONAL_DEVELOPMENT__PHYSICS_MOVEMENT_ID:
      return new PhysicsMovementPdfDataProcessor(baseData, t).process();

    case PERSONAL_DEVELOPMENT__RECREATIONAL_ARTISTIC_ID:
      return new RecreationalArtisticPdfDataProcessor(baseData, t).process();

    case PERSONAL_DEVELOPMENT__SOCIAL_INTERACTION_ID:
      return new SocialInteractionPdfDataProcessor(baseData, t).process();

    case PERSONAL_DEVELOPMENT__RELIGION_SPIRITUALITY_ID:
      return new ReligionSpiritualityPdfDataProcessor(baseData, t).process();

    case PERSONAL_DEVELOPMENT__SOCIOCULTURAL_ID:
      return new SocioculturalPdfDataProcessor(baseData, t).process();

    case PERSONAL_DEVELOPMENT__DAILY_OCCUPATION_ID:
      return new DailyOccupationPdfDataProcessor(baseData, t).process();

    case PERSONAL_DEVELOPMENT__SENSORY_ID:
      return new SensoryPdfDataProcessor(baseData, t).process();

    default:
      return new ExportCarePlanGroupDataProcessor(baseData, t).process();
  }
}
